import type { MutableRefObject } from 'react'
import { useCallback, useContext, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import LineArrowsSwapHorizontalIcon from 'LuxKit/Icons/line/LineArrowsSwapHorizontalIcon'
import FlightSearchAirportSelect from './FlightAirportSelect/FlightSearchAirportSelect'
import IconButton from 'LuxKit/Button/IconButton'
import clsx from 'clsx'
import { rem } from 'polished'

const InputContainer = styled(SearchFormFieldGroup)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  position: relative;

  > .arrival-select {
    border-inline-start: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  .arrival-select {
    &--read-only{
      padding-inline-start: ${rem(32)};
    }

    .display-field,  input {
      padding-inline-start: ${rem(32)};
    }
  }
`

const ExchangeButtonContainer = styled.div`
  position: absolute;
  inset-inline-start: 50%;
  transform: translateX(-50%);
  z-index: 1;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  [dir='rtl'] & {
    transform: translateX(50%);
  }
`

interface Props {
  className?: string
  readOnly?: boolean
  id: string
  onArrivalChange?: (airport: App.AirportLocation) => void
  onDepartureChange?: (airport: App.AirportLocation) => void
  arrivalFieldRef?: MutableRefObject<HTMLInputElement | null> | ((node: HTMLInputElement) => void)
  departingFieldRef?: MutableRefObject<HTMLInputElement | null> | ((node: HTMLInputElement) => void)
  departingRecentAirports?: Array<App.AirportLocation>
  groupAirportDropdowns?: boolean
}

function FlightSearchAirportInputs(props: Props) {
  const { className, readOnly, id, onArrivalChange, onDepartureChange, departingFieldRef, arrivalFieldRef, departingRecentAirports, groupAirportDropdowns } = props
  const intl = useIntl()
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const inputContainerRef = useRef<HTMLDivElement>(null)

  const currentFlight = useMemo(() => {
    return state.flights.find((flight) => flight.id === id)
  }, [state.flights, id])

  const departureAirport = currentFlight?.departureAirport
  const arrivalAirport = currentFlight?.arrivalAirport
  const onSwapAirports = useCallback(() => {
    const departure = departureAirport
    dispatch({ type: FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT, id, airport: arrivalAirport })
    dispatch({ type: FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT, id, airport: departure })
  }, [departureAirport, dispatch, arrivalAirport, id])

  const onDepartureSelect = useCallback((airport: App.AirportLocation) => {
    dispatch({ type: FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT, id, airport })
    onDepartureChange?.(airport)
  }, [dispatch, id, onDepartureChange])

  const onArrivalSelect = useCallback((airport: App.AirportLocation) => {
    dispatch({ type: FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT, id, airport })
    onArrivalChange?.(airport)
  }, [dispatch, id, onArrivalChange])

  const fromLabel = useMemo(
    () =>
      intl.formatMessage({
        id: 'ZGF6ICItXlqbkxqPyUHc',
        defaultMessage: 'From',
        description: 'Label for departure airport field in flight search',
      }),
    [intl],
  )

  const toLabel = useMemo(
    () =>
      intl.formatMessage({
        id: '2UOCctw7EHd0XUmxtgr3',
        defaultMessage: 'To',
        description: 'Form field label for arrival airport field in flight search',
      }),
    [intl],
  )

  const recentSearchHeading = useMemo(
    () =>
      departingRecentAirports
        ? intl.formatMessage({
            id: 'CZnlPtv3n6gX-dddYKi2',
            defaultMessage: 'Recent search',
            description: 'Heading for recent airport searches in flight search',
          })
        : undefined,
    [intl, departingRecentAirports],
  )

  return (
    <InputContainer className={className} ref={inputContainerRef}>
      <FlightSearchAirportSelect
        label={fromLabel}
        required
        direction="departure"
        onSelectAirport={onDepartureSelect}
        airport={departureAirport}
        alternateAirport={arrivalAirport}
        readOnly={Boolean(readOnly)}
        ref={departingFieldRef}
        fallbackAirports={departingRecentAirports}
        fallbackHeading={recentSearchHeading}
        dropdownAnchorRef={groupAirportDropdowns ? inputContainerRef : undefined}
      />

      <FlightSearchAirportSelect
        label={toLabel}
        required
        direction="arrival"
        onSelectAirport={onArrivalSelect}
        airport={arrivalAirport}
        alternateAirport={departureAirport}
        readOnly={Boolean(readOnly)}
        ref={arrivalFieldRef}
        className={clsx('arrival-select', {
          'arrival-select--read-only': readOnly,
        })}
        dropdownAnchorRef={groupAirportDropdowns ? inputContainerRef : undefined}
      />

      <ExchangeButtonContainer className="swap">
        <IconButton variant="default" kind="secondary" size="small" shape="circle" onClick={onSwapAirports} disabled={readOnly}>
          <LineArrowsSwapHorizontalIcon size="S" />
        </IconButton>
      </ExchangeButtonContainer>
    </InputContainer>
  )
}

export default FlightSearchAirportInputs
